import React, { useEffect, useMemo, useState } from 'react';
import {
  Check,
  CircleCheck,
  ClipboardCheck,
  Ruler,
  Save,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  ThicknessCADVerificationMode,
  ThicknessRegionDisposition,
  ThicknessRegionReview,
} from '../../../types/cad';
import { ThicknessPanelController } from './useCADThicknessPanel';

interface ThicknessReviewSectionProps {
  ctrl: ThicknessPanelController;
  uid: string;
  canAnalyze: boolean;
  canApprove: boolean;
}

const EMPTY_REGION_REVIEW: ThicknessRegionReview = {
  analysisConfirmed: false,
  cadVerificationMode: 'analysis_value',
  cadMeasuredMm: null,
  measurementMethod: '',
  disposition: 'unreviewed',
  rationale: '',
  assignee: '',
  dueDate: null,
  completedBy: null,
  completedByName: null,
  completedAt: null,
};

const STEP_KEYS = [
  'analysisCheck',
  'cadVerification',
  'designAction',
  'saveExport',
] as const;

export const ThicknessReviewSection: React.FC<ThicknessReviewSectionProps> = ({
  ctrl,
  uid,
  canAnalyze,
  canApprove,
}) => {
  const {
    t,
    review,
    selectedRegion,
    regionDispositions,
    setRegionDispositions,
    regionReviews,
    setRegionReviews,
    reviewComment,
    setReviewComment,
    savingReview,
    reviewDirty,
    saveReview,
  } = ctrl;
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    setActiveStep(0);
  }, [selectedRegion?.regionId]);

  const selectedReview = useMemo(() => {
    if (!selectedRegion) return EMPTY_REGION_REVIEW;
    const detailed = regionReviews[selectedRegion.regionId];
    if (detailed) return detailed;
    return {
      ...EMPTY_REGION_REVIEW,
      disposition: regionDispositions[selectedRegion.regionId] || 'unreviewed',
    };
  }, [regionDispositions, regionReviews, selectedRegion]);

  const updateSelectedReview = (patch: Partial<ThicknessRegionReview>) => {
    if (!selectedRegion) return;
    const next = { ...selectedReview, ...patch };
    setRegionReviews((current) => ({
      ...current,
      [selectedRegion.regionId]: next,
    }));
    if (patch.disposition) {
      setRegionDispositions((current) => ({
        ...current,
        [selectedRegion.regionId]: patch.disposition as ThicknessRegionDisposition,
      }));
    }
  };

  const analysisValue = selectedRegion
    ? selectedRegion.judgment === 'thin'
      ? selectedRegion.minMm
      : selectedRegion.maxMm
    : null;
  const measuredDifference = (
    analysisValue !== null && selectedReview.cadMeasuredMm !== null
      ? selectedReview.cadMeasuredMm - analysisValue
      : null
  );

  return (
    <section aria-labelledby={`${uid}-guided-review`}>
      <Separator className="my-3" />
      <div className="flex items-center justify-between gap-2">
        <p id={`${uid}-guided-review`} className="text-sm font-bold text-foreground">
          {t('cad.thickness.review.stepFlow')}
        </p>
        <Badge
          variant={
            review?.status === 'approved'
              ? 'success'
              : review?.status === 'rejected'
                ? 'destructive'
                : 'secondary'
          }
        >
          {t(`cad.thickness.review.status.${review?.status || 'unreviewed'}`)}
        </Badge>
      </div>

      <ol className="mt-2 grid grid-cols-4 gap-1" aria-label={t('cad.thickness.review.stepsLabel')}>
        {STEP_KEYS.map((stepKey, index) => (
          <li key={stepKey}>
            <button
              type="button"
              onClick={() => setActiveStep(index)}
              className={cn(
                'flex min-h-14 w-full flex-col items-center justify-center rounded-md border px-1 text-center text-[11px]',
                activeStep === index
                  ? 'border-primary bg-primary/10 font-bold text-primary'
                  : 'border-border text-muted-foreground hover:bg-accent',
              )}
            >
              <span className="mb-0.5 text-xs">{index + 1}</span>
              {t(`cad.thickness.review.steps.${stepKey}`)}
            </button>
          </li>
        ))}
      </ol>

      {!selectedRegion ? (
        <Alert variant="info" className="mt-2">
          <AlertDescription>
            {t('cad.thickness.review.selectRegionHint')}
          </AlertDescription>
        </Alert>
      ) : (
        <div className="mt-2 rounded-md border border-border p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-sm font-semibold">{selectedRegion.regionId}</span>
            <Badge variant={selectedRegion.judgment === 'thin' ? 'destructive' : 'info'}>
              {analysisValue?.toFixed(3)} mm
            </Badge>
          </div>

          {activeStep === 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`${uid}-analysis-confirmed`}
                  checked={selectedReview.analysisConfirmed}
                  onCheckedChange={(checked) => updateSelectedReview({
                    analysisConfirmed: checked === true,
                  })}
                  disabled={!canAnalyze || savingReview}
                />
                <Label htmlFor={`${uid}-analysis-confirmed`}>
                  {t('cad.thickness.review.analysisConfirmed')}
                </Label>
              </div>
              <dl className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded bg-muted p-2">
                  <dt className="text-muted-foreground">{t('cad.thickness.review.violationRegion')}</dt>
                  <dd className="font-semibold">
                    {t('cad.thickness.review.regionPointCount', { count: selectedRegion.count })}
                  </dd>
                </div>
                <div className="rounded bg-muted p-2">
                  <dt className="text-muted-foreground">{t('cad.thickness.review.representative')}</dt>
                  <dd className="font-semibold">
                    {t('cad.thickness.review.representativeSummary', {
                      min: selectedRegion.minMm.toFixed(3),
                      mean: selectedRegion.meanMm.toFixed(3),
                    })}
                  </dd>
                </div>
              </dl>
              <Alert variant="warning">
                <AlertDescription className="text-xs">
                  {t('cad.thickness.review.accuracyDisclaimer')}
                </AlertDescription>
              </Alert>
            </div>
          )}

          {activeStep === 1 && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">{t('cad.thickness.review.cadVerificationLabel')}</Label>
                <div className="mt-1 grid grid-cols-3 gap-1">
                  {([
                    'analysis_value',
                    'cad_measured',
                    'reanalyze',
                  ] as ThicknessCADVerificationMode[]).map((value) => (
                    <Button
                      key={value}
                      type="button"
                      size="sm"
                      variant={selectedReview.cadVerificationMode === value ? 'default' : 'outline'}
                      onClick={() => updateSelectedReview({ cadVerificationMode: value })}
                      disabled={!canAnalyze || savingReview}
                      className="h-auto min-h-9 whitespace-normal px-1 text-xs"
                    >
                      {value === 'cad_measured' && <Ruler />}
                      {t(`cad.thickness.review.cadVerificationMode.${value}`)}
                    </Button>
                  ))}
                </div>
              </div>
              {selectedReview.cadVerificationMode === 'cad_measured' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor={`${uid}-cad-value`} className="text-xs">{t('cad.thickness.review.cadMeasured')}</Label>
                    <Input
                      id={`${uid}-cad-value`}
                      type="number"
                      min={0}
                      step={0.001}
                      value={selectedReview.cadMeasuredMm ?? ''}
                      onChange={(event) => updateSelectedReview({
                        cadMeasuredMm: event.target.value === '' ? null : Number(event.target.value),
                      })}
                      disabled={!canAnalyze || savingReview}
                    />
                  </div>
                  <div className="rounded bg-muted p-2 text-xs">
                    <span className="text-muted-foreground">{t('cad.thickness.review.analysisDifference')}</span>
                    <strong className="mt-1 block text-sm">
                      {measuredDifference === null ? '-' : `${measuredDifference >= 0 ? '+' : ''}${measuredDifference.toFixed(3)} mm`}
                    </strong>
                  </div>
                </div>
              )}
              <div>
                <Label htmlFor={`${uid}-measurement-method`} className="text-xs">{t('cad.thickness.review.measurementMethod')}</Label>
                <Textarea
                  id={`${uid}-measurement-method`}
                  rows={2}
                  value={selectedReview.measurementMethod}
                  onChange={(event) => updateSelectedReview({ measurementMethod: event.target.value })}
                  placeholder={t('cad.thickness.review.measurementMethodPlaceholder')}
                  disabled={!canAnalyze || savingReview}
                />
              </div>
            </div>
          )}

          {activeStep === 2 && (
            <div className="space-y-3">
              <div>
                <Label htmlFor={`${uid}-disposition`} className="text-xs">{t('cad.thickness.review.steps.designAction')}</Label>
                <Select
                  value={selectedReview.disposition}
                  onValueChange={(value) => updateSelectedReview({
                    disposition: value as ThicknessRegionDisposition,
                  })}
                  disabled={!canAnalyze || savingReview}
                >
                  <SelectTrigger id={`${uid}-disposition`} className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {([
                      'unreviewed',
                      'requires_change',
                      'accepted',
                      'false_positive',
                      'needs_confirmation',
                    ] as ThicknessRegionDisposition[]).map((value) => (
                      <SelectItem key={value} value={value}>
                        {t(`cad.thickness.review.disposition.${value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor={`${uid}-rationale`} className="text-xs">{t('cad.thickness.review.rationale')}</Label>
                <Textarea
                  id={`${uid}-rationale`}
                  rows={2}
                  value={selectedReview.rationale}
                  onChange={(event) => updateSelectedReview({ rationale: event.target.value })}
                  disabled={!canAnalyze || savingReview}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor={`${uid}-assignee`} className="text-xs">{t('cad.thickness.review.assignee')}</Label>
                  <Input
                    id={`${uid}-assignee`}
                    value={selectedReview.assignee}
                    onChange={(event) => updateSelectedReview({ assignee: event.target.value })}
                    disabled={!canAnalyze || savingReview}
                  />
                </div>
                <div>
                  <Label htmlFor={`${uid}-due-date`} className="text-xs">{t('cad.thickness.review.dueDate')}</Label>
                  <Input
                    id={`${uid}-due-date`}
                    type="date"
                    value={selectedReview.dueDate || ''}
                    onChange={(event) => updateSelectedReview({ dueDate: event.target.value || null })}
                    disabled={!canAnalyze || savingReview}
                  />
                </div>
              </div>
            </div>
          )}

          {activeStep === 3 && (
            <div className="space-y-3">
              <div className="rounded-md bg-muted p-2 text-xs">
                <p className="flex items-center gap-1 font-semibold"><ClipboardCheck className="size-4" />{t('cad.thickness.review.evidenceTitle')}</p>
                <p className="mt-1 text-muted-foreground">
                  {t(
                    selectedReview.cadMeasuredMm !== null
                      ? 'cad.thickness.review.evidenceSummaryMeasured'
                      : 'cad.thickness.review.evidenceSummary',
                    {
                      analysis: analysisValue?.toFixed(3) ?? '-',
                      mode: t(`cad.thickness.review.cadVerificationMode.${selectedReview.cadVerificationMode}`),
                      measured: selectedReview.cadMeasuredMm?.toFixed(3),
                      disposition: t(`cad.thickness.review.disposition.${selectedReview.disposition}`),
                    },
                  )}
                </p>
                {selectedReview.completedAt && (
                  <p className="mt-1 text-muted-foreground">
                    {t('cad.thickness.review.lastCompleted', {
                      name: selectedReview.completedByName || selectedReview.completedBy,
                      at: new Date(selectedReview.completedAt).toLocaleString(),
                    })}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="mt-3 flex justify-between">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setActiveStep((current) => Math.max(0, current - 1))}
              disabled={activeStep === 0}
            >
              {t('cad.thickness.review.back')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setActiveStep((current) => Math.min(3, current + 1))}
              disabled={activeStep === 3}
            >
              {activeStep < 3 && <Check />}{t('cad.thickness.review.next')}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-2 rounded-md border border-border p-2">
        <Label htmlFor={`${uid}-comment`} className="text-xs">
          {t('cad.thickness.review.comment')}
        </Label>
        <Textarea
          id={`${uid}-comment`}
          rows={2}
          value={reviewComment}
          onChange={(event) => setReviewComment(event.target.value)}
          disabled={!canAnalyze || savingReview}
        />
        {canAnalyze && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              onClick={() => saveReview('in_review')}
              disabled={savingReview}
            >
              <Save />{t('cad.thickness.review.save')}
            </Button>
            {canApprove && (
              <Button
                onClick={() => saveReview('approved')}
                disabled={savingReview}
                className="bg-success text-success-foreground hover:bg-success/90"
              >
                <CircleCheck />{t('cad.thickness.review.approve')}
              </Button>
            )}
            {canApprove && (
              <Button
                variant="outline"
                onClick={() => saveReview('rejected')}
                disabled={savingReview}
                className="col-span-2 border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                {t('cad.thickness.review.reject')}
              </Button>
            )}
          </div>
        )}
        {reviewDirty && (
          <p className="mt-1 text-[11px] text-warning">{t('cad.thickness.review.unsavedChanges')}</p>
        )}
      </div>
    </section>
  );
};
